import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Switch,
  Alert,
  Skeleton,
  Snackbar,
  Divider,
  Grid,
  alpha,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import {
  finkedaAdminConfig,
  setFinkedaCharge,
  setFinkedaGateway,
  setFinkedaSettings,
  setFinkedaPayoutSlabs,
  finkedaBalance,
} from "../api";

/* ---------------------------------- helpers ---------------------------------- */

const cleanLabel = (s) =>
  String(s || "")
    .replace(/finkeda/gi, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "—";

const num = (v) => (v == null || v === "" ? "" : String(v));

/* ================================== page ================================== */

export default function FinkedaAdminConfig() {
  const [gateways, setGateways] = useState([]);
  const [settings, setSettings] = useState({ flatAdminFee: "", selfShareMaxPct: "" });
  const [payout, setPayout] = useState({ slabs: [], gstPct: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [balance, setBalance] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    finkedaAdminConfig()
      .then(({ data }) => {
        setGateways(data.gateways || []);
        setSettings({
          flatAdminFee: num(data.settings?.flatAdminFee),
          selfShareMaxPct: num(data.settings?.selfShareMaxPct),
        });
        setPayout({
          slabs: (data.payout?.slabs || []).map((s) => ({
            upTo: num(s.upTo),
            fee: num(s.fee),
          })),
          gstPct: num(data.payout?.gstPct),
        });
      })
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load configuration"),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    finkedaBalance()
      .then(({ data }) => setBalance(data))
      .catch(() => setBalance(null));
  }, []);

  /* mutate a single charge row in local state */
  const patchCharge = (gk, idx, field, value) =>
    setGateways((prev) =>
      prev.map((g) =>
        g.gatewayKey !== gk
          ? g
          : {
              ...g,
              charges: g.charges.map((c, i) =>
                i === idx ? { ...c, [field]: value } : c,
              ),
            },
      ),
    );

  /* Save EVERYTHING for a gateway in one action: its pgType/active flag AND
     every charge row (rate + limit). This is what the "Save" button does, so
     editing a percentage and hitting Save actually persists it. */
  const saveGateway = async (g) => {
    for (const c of g.charges || []) {
      if (!(Number(c.finkedaChargePct) >= 0)) {
        setSnack({
          severity: "error",
          msg: `Charge % for ${c.network}/${c.cardType} must be zero or positive`,
        });
        return;
      }
    }
    setSavingKey(`gw:${g.gatewayKey}`);
    try {
      await setFinkedaGateway(g.gatewayKey, {
        pgType: g.pgType,
        active: g.active !== false,
      });
      for (const c of g.charges || []) {
        const cf = Number(c.finkedaChargePct);
        await setFinkedaCharge({
          gatewayKey: g.gatewayKey,
          network: c.network,
          cardType: c.cardType,
          finkedaChargePct: cf,
          /* admin_charge_pct is legacy/unused by the money model — the admin's
             real margin comes from the Overrides screen. Keep it == cf so the
             backend's "cp >= cf" guard passes. */
          adminChargePct: cf,
          txnLimit:
            c.txnLimit === "" || c.txnLimit == null ? null : Number(c.txnLimit),
        });
      }
      setSnack({ severity: "success", msg: "Gateway & rates saved" });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to save gateway",
      });
    }
    setSavingKey(null);
  };

  const saveSettings = async () => {
    setSavingKey("settings");
    try {
      await setFinkedaSettings({
        flatAdminFee: Number(settings.flatAdminFee) || 0,
        selfShareMaxPct: Number(settings.selfShareMaxPct) || 0,
      });
      setSnack({ severity: "success", msg: "Settings saved" });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to save settings",
      });
    }
    setSavingKey(null);
  };

  const patchSlab = (idx, field, value) =>
    setPayout((p) => ({
      ...p,
      slabs: p.slabs.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
    }));

  const addSlab = () =>
    setPayout((p) => ({ ...p, slabs: [...p.slabs, { upTo: "", fee: "" }] }));

  const removeSlab = (idx) =>
    setPayout((p) => ({ ...p, slabs: p.slabs.filter((_, i) => i !== idx) }));

  const savePayout = async () => {
    setSavingKey("payout");
    try {
      const { data } = await setFinkedaPayoutSlabs({
        gstPct: Number(payout.gstPct) || 0,
        slabs: payout.slabs.map((s) => ({
          upTo: s.upTo === "" || s.upTo == null ? null : Number(s.upTo),
          fee: Number(s.fee) || 0,
        })),
      });
      setPayout({
        slabs: (data?.slabs || []).map((s) => ({
          upTo: num(s.upTo),
          fee: num(s.fee),
        })),
        gstPct: num(data?.gstPct),
      });
      setSnack({ severity: "success", msg: "Payout fees saved" });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to save payout fees",
      });
    }
    setSavingKey(null);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Card→Bank Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gateway charges, limits and platform fee. Commission shares are set
            per-user in the Overrides screen.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {balance && (
            <Chip
              icon={<AccountBalanceRoundedIcon />}
              label={`Provider balance: ₹${Number(
                balance.balance ?? balance.available ?? 0,
              ).toLocaleString("en-IN")}`}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Tooltip title="Reload" arrow>
            <IconButton size="small" onClick={load}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* ------------------------- always-visible explainer ------------------------- */}
      <Card
        sx={{
          my: 2,
          p: 2.5,
          borderRadius: 3,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
        }}
      >
        <Typography fontWeight={700} mb={1}>
          How Card→Bank works
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          A customer pays with a card on the provider's hosted page. The provider
          keeps its <b>charge %</b> (set below) + a flat <b>payout fee</b>; the
          rest is paid straight to the beneficiary's bank. Every participant's
          commission is set <b>per-user in the Overrides screen</b> and credited
          to their own Card→Bank wallet.
          <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
            <li>
              <b>Charge %</b> (below) = the provider's real cut. Edit it here per
              gateway/network.
            </li>
            <li>
              <b>Admin override</b> = a <b>bundled</b> rate (this charge % + your
              own margin). The system nets the charge out, so you're credited{" "}
              <b>only your margin</b> — and reports show that real margin (just
              like the 0.17% payin factor).
            </li>
            <li>
              <b>Distributors / retailers</b> earn exactly their override %.
            </li>
            <li>
              <b>Flat platform fee</b> (below) always goes to admin.
            </li>
          </Box>
          <Box sx={{ mt: 1.5, fontSize: 13 }}>
            <b>Example</b> — ₹10,000, charge 1.5%, admin override 2%, SD 0.5%:
            provider keeps ₹150 + payout fee; admin credited{" "}
            <b>₹50 (the real 0.5%)</b>; SD credited ₹50; ≈₹9,744 to the bank.
          </Box>
        </Typography>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && !error && gateways.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          No gateways loaded. Make sure the backend is running the latest build
          and the Finkeda migrations have run, then hit reload.
        </Alert>
      )}

      {/* ------------------------------- gateways ------------------------------- */}
      {loading ? (
        <Card sx={{ p: 3, borderRadius: 3 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height={48} sx={{ mb: 1 }} />
          ))}
        </Card>
      ) : (
        <Stack spacing={2.5}>
          {gateways.map((g) => (
            <Card key={g.gatewayKey} sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography fontWeight={700}>
                    {g.purpose || cleanLabel(g.gatewayKey)}
                  </Typography>
                  {g.mcc && (
                    <Chip size="small" label={`MCC ${g.mcc}`} variant="outlined" />
                  )}
                  <Chip
                    size="small"
                    label={g.active !== false ? "Active" : "Disabled"}
                    color={g.active !== false ? "success" : "default"}
                    variant={g.active !== false ? "filled" : "outlined"}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <TextField
                    size="small"
                    label="Provider gateway type"
                    value={g.pgType || ""}
                    onChange={(e) =>
                      setGateways((prev) =>
                        prev.map((x) =>
                          x.gatewayKey === g.gatewayKey
                            ? { ...x, pgType: e.target.value }
                            : x,
                        ),
                      )
                    }
                    sx={{ width: 190 }}
                  />
                  <Tooltip title={g.active !== false ? "Enabled" : "Disabled"} arrow>
                    <Switch
                      checked={g.active !== false}
                      onChange={(e) =>
                        setGateways((prev) =>
                          prev.map((x) =>
                            x.gatewayKey === g.gatewayKey
                              ? { ...x, active: e.target.checked }
                              : x,
                          ),
                        )
                      }
                    />
                  </Tooltip>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    disabled={savingKey === `gw:${g.gatewayKey}`}
                    onClick={() => saveGateway(g)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                  >
                    Save gateway & rates
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: 700,
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          color: "text.secondary",
                        },
                      }}
                    >
                      <TableCell>Network</TableCell>
                      <TableCell>Card type</TableCell>
                      <TableCell>Charge % (provider)</TableCell>
                      <TableCell>Txn limit (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(g.charges || []).map((c, idx) => {
                      return (
                        <TableRow key={`${c.network}:${c.cardType}`} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{c.network}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>
                            {c.cardType}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={num(c.finkedaChargePct)}
                              onChange={(e) =>
                                patchCharge(
                                  g.gatewayKey,
                                  idx,
                                  "finkedaChargePct",
                                  e.target.value,
                                )
                              }
                              InputProps={{ endAdornment: "%" }}
                              sx={{ width: 130 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="No limit"
                              value={num(c.txnLimit)}
                              onChange={(e) =>
                                patchCharge(
                                  g.gatewayKey,
                                  idx,
                                  "txnLimit",
                                  e.target.value,
                                )
                              }
                              sx={{ width: 150 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(g.charges || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                          No charge rows configured for this gateway.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      {/* ------------------------------- settings ------------------------------- */}
      <Card sx={{ borderRadius: 3, p: 2.5, mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <TuneRoundedIcon color="primary" fontSize="small" />
          <Typography fontWeight={700}>Platform settings</Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Flat platform fee (₹)"
              helperText="Credited directly to admin per transaction"
              value={settings.flatAdminFee}
              onChange={(e) =>
                setSettings((s) => ({ ...s, flatAdminFee: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max self-share (%)"
              helperText="Cap on the payer's own markup per txn"
              value={settings.selfShareMaxPct}
              onChange={(e) =>
                setSettings((s) => ({ ...s, selfShareMaxPct: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              disabled={savingKey === "settings"}
              onClick={saveSettings}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
            >
              Save settings
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          mb={0.5}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Payout service fee (provider)
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              type="number"
              label="GST %"
              value={payout.gstPct}
              onChange={(e) =>
                setPayout((p) => ({ ...p, gstPct: e.target.value }))
              }
              sx={{ width: 110 }}
            />
            <Button
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={addSlab}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Add slab
            </Button>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Flat fee the provider charges per payout, deducted from the amount to
          bank. A payout uses the first slab whose upper bound ≥ the amount;
          leave the last slab's bound blank to cover everything above.
        </Typography>
        <Box sx={{ overflowX: "auto", mt: 1 }}>
          <Table size="small" sx={{ maxWidth: 520 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Up to (₹)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fee (₹, before GST)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {payout.slabs.map((s, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="No upper limit"
                      value={s.upTo}
                      onChange={(e) => patchSlab(idx, "upTo", e.target.value)}
                      sx={{ width: 160 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={s.fee}
                      onChange={(e) => patchSlab(idx, "fee", e.target.value)}
                      sx={{ width: 140 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeSlab(idx)}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {payout.slabs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                    No slabs — add one, or the built-in defaults are used.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveRoundedIcon />}
          disabled={savingKey === "payout"}
          onClick={savePayout}
          sx={{ mt: 1.5, borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
        >
          Save payout fees
        </Button>
      </Card>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            sx={{ borderRadius: 2 }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
